import { ITerraceMeasure } from '../../utility/interface/terraceCover.interface'
import { TAttachment } from '../../utility/type/terraceCover.type'

/**
 * Implements helper methods that can be used across the app
 * @author Sandro Müller
 * @remarks No remarks
 */
export abstract class Helper {
  /**
   * Converts positive angle values to radiant value
   * @returns The converted radiant
   */
  public static convertAngleToRadiant(angle: number): number {
    return ((angle % 360) * Math.PI) / 180
  }

  /**
   * Converts centimeter input to meter
   * @param cm The input that is converted to meter
   * @returns Centimeter expressed in meter
   */
  public static convertToMeter(cm: number): number {
    return cm / 100
  }

  /**
   * Custom iterator mimic python's range function
   * @param start The value the iterator starts with
   * @param end  The end value the iterator ends with
   * @param step The step taken with each iteration
   * @example <caption>As part of a for-loop</caption>
   * for(const n of range(0, 20, 5)) { ... }
   */
  public static range(start: number, end: number, step = 1) {
    return {
      [Symbol.iterator]() {
        return this
      },

      next() {
        if (start < end - 1) {
          start += step
          return { value: start, done: false }
        }

        return { done: true, value: end }
      },
    }
  }

  /**
   * Capitalize the first letter of a given word
   * @param word The word that gets capitalized
   * @returns A capitalized string
   */
  public static capitalize(word: string): string {
    return word.charAt(0).toUpperCase() + word.slice(1)
  }

  /**
   * Calculates the start position for the terrace cover as it can be detached
   * @param attachment Current value that decides if the terrace cover is detached
   * @returns The start position based on the current attachment value
   */
  public static coverStartPosition(attachment: TAttachment): number {
    // TODO
    if (attachment === 'Freistehend') return 0.5

    return 0
  }

  /**
   * Due to the roation by angle, the elements have to be extended
   * @param angle The angle set by the user
   * @returns
   */
  public static extendTopElements(angle: number) {
    const amount = {
      0: 0,
      1: 0.2,
      2: 0.45,
      3: 0.8,
      4: 1.25,
      5: 1.8,
      6: 2.45,
      7: 3.2,
      8: 4,
      9: 4.9,
      10: 5.95,
      11: 7.1,
      12: 8.3,
      13: 9.6,
      14: 11,
    }

    return amount[angle as keyof typeof amount] ?? 0
  }

  /**
   * A method that helps to decide which windows need to overlap
   * @param measure The measure context values
   * @param isMoonRoof If the moon roof setting is set
   * @param index The postion if the window
   * @returns The overlap for the front windows
   */
  public static windowOverlap(
    measure: ITerraceMeasure,
    isMoonRoof: boolean,
    index: number
  ): number {
    const isEven = measure.windowsFrontTotal % 2 === 0
    const windowCenter =
      index === measure.windowsFrontTotal / 2 ||
      index === measure.windowsFrontTotal / 2 - 1

    return isMoonRoof && !(isEven && windowCenter) ? 4 : 0
  }
}
